import {
  contrast,
  editorialPalette,
  FONT_CHOICES,
  planLimitMessage,
  PLANS,
  FONT_STACKS,
  isHexColor,
  readableAccent,
  type BrandSettings,
  type FontChoice,
} from '@nolens/domain';
import { Banner, Button, Field, Input, Select, useLocale, useToast } from '@nolens/ui';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { QueryView } from '../components/Query';
import { ApiError } from '../lib/api';
import { brandStyle, useMemberBrandAssets } from '../lib/brand';
import { useBrand, useBrandActions } from '../lib/queries';
import { spaceCrumbs, WithMe } from './workspace';
import { shellText } from '../i18n/shell';
import { spaceName } from '../lib/locale';
import { publicPaths } from '../i18n/landing';
import { brandText } from '../i18n/brand';

/** Kit de marca de um espaço (só o dono): empresa, cores, logótipo, letra e rodapé. */
export function BrandPage() {
  const { workspaceId } = useParams();
  const locale = useLocale();
  const t = brandText(locale);
  const pricingPath = publicPaths(locale).pricing;
  const brand = useBrand(workspaceId);
  const st = shellText(locale);
  return (
    <WithMe
      current={workspaceId}
      settings
      crumbs={(me) =>
        spaceCrumbs(
          me,
          workspaceId,
          locale,
          { label: st.settings, to: `/w/${workspaceId}/definicoes` },
          { label: st.brand },
        )
      }
    >
      {(me) => {
        const ws = me.workspaces.find((w) => w.id === workspaceId);
        if (!ws) return <p className="text-text-2">{t.spaceNotFound}</p>;
        if (ws.role !== 'owner') return <p className="text-text-2">{t.ownerOnly}</p>;
        return (
          <>
            {!PLANS[ws.plan].brand && (
              <Banner tone="attention" className="mb-5">
                {planLimitMessage('brand', locale)} {t.tryHere}{' '}
                <Link to={pricingPath} className="font-medium underline">
                  {t.seePlans}
                </Link>
              </Banner>
            )}
            <QueryView q={brand}>
              {(b) => <BrandForm ws={ws.id} name={spaceName(ws, locale)} brand={b} />}
            </QueryView>
          </>
        );
      }}
    </WithMe>
  );
}

function BrandForm({ ws, name, brand }: { ws: string; name: string; brand: BrandSettings | null }) {
  const act = useBrandActions(ws);
  const toast = useToast();
  const t = brandText(useLocale());
  const [form, setForm] = useState({
    accent: brand?.accent ?? '#C2410C',
    font: (brand?.font ?? 'inter') as FontChoice,
    footer: brand?.footer ?? '',
    companyName: brand?.companyName ?? '',
    coverColor: brand?.coverColor ?? null,
    highlightColor: brand?.highlightColor ?? null,
  });
  const colorsOk =
    isHexColor(form.accent) &&
    (form.coverColor === null || isHexColor(form.coverColor)) &&
    (form.highlightColor === null || isHexColor(form.highlightColor));
  const preview: BrandSettings = {
    ...form,
    logoVersion: brand?.logoVersion ?? null,
    fontVersion: brand?.fontVersion ?? null,
  };
  const assets = useMemberBrandAssets(ws, brand ? { ...preview } : null);
  const error = [act.save, act.upload, act.removeFile]
    .map((m) => m.error)
    .find((e) => e instanceof ApiError) as ApiError | undefined;
  const weak = isHexColor(form.accent) && contrast(form.accent, '#ffffff') < 4.5;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    act.save.mutate(form, { onSuccess: () => toast(t.saved, 'positive') });
  };
  const upload = (what: 'logo' | 'font') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file)
      act.upload.mutate(
        { what, file },
        {
          onSuccess: () => toast(what === 'logo' ? t.logoUploaded : t.fontUploaded, 'positive'),
        },
      );
  };

  return (
    <section className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <form onSubmit={submit} className="flex flex-col gap-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t.title(name)}</h1>
          <p className="mt-1 text-sm text-text-2">{t.hint}</p>
        </div>
        {error && <Banner tone="critical">{error.message}</Banner>}
        <Field label={t.companyName} hint={t.companyNameHint(name)}>
          {(p) => (
            <Input
              {...p}
              maxLength={80}
              placeholder={name}
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t.mainColor}
          hint={weak ? t.weakColor(readableAccent(form.accent)) : undefined}
        >
          {(p) => (
            <div className="flex items-center gap-3">
              <input
                type="color"
                aria-label={t.pickColor}
                className="size-11 cursor-pointer rounded-[10px] border-0 bg-transparent"
                value={isHexColor(form.accent) ? form.accent : '#000000'}
                onChange={(e) => setForm({ ...form, accent: e.target.value.toUpperCase() })}
              />
              <Input
                {...p}
                className="w-32 font-mono"
                value={form.accent}
                maxLength={7}
                onChange={(e) => setForm({ ...form, accent: e.target.value })}
              />
            </div>
          )}
        </Field>
        <OptionalColor
          label={t.coverColor}
          hint={t.coverColorHint}
          auto={'#' + editorialPalette(form.accent).dark}
          value={form.coverColor}
          onChange={(v) => setForm({ ...form, coverColor: v })}
        />
        <OptionalColor
          label={t.highlightColor}
          hint={t.highlightColorHint}
          auto={'#' + editorialPalette(form.accent).highlight}
          value={form.highlightColor}
          onChange={(v) => setForm({ ...form, highlightColor: v })}
        />
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t.logo}</span>
          <div className="flex flex-wrap items-center gap-3">
            {assets.logo && (
              <img
                src={assets.logo}
                alt={t.currentLogo}
                className="h-10 w-auto max-w-[180px] rounded bg-surface-1 p-1"
              />
            )}
            <label className="inline-flex h-9 cursor-pointer items-center rounded-full px-4 text-sm shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-1">
              {brand?.logoVersion ? t.replace : t.upload}
              {t.logoFormats}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                onChange={upload('logo')}
              />
            </label>
            {brand?.logoVersion && (
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => act.removeFile.mutate('logo')}
              >
                {t.remove}
              </Button>
            )}
          </div>
        </div>
        <Field label={t.font}>
          {(p) => (
            <Select
              {...p}
              value={form.font}
              onChange={(e) => setForm({ ...form, font: e.target.value as FontChoice })}
            >
              {FONT_CHOICES.map((f) => (
                <option key={f} value={f} disabled={f === 'custom' && !brand?.fontVersion}>
                  {t.fontName(f)}
                  {f === 'custom' && !brand?.fontVersion ? t.uploadFontFirst : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <label className="inline-flex h-9 w-fit cursor-pointer items-center rounded-full px-4 text-sm shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-1">
          {brand?.fontVersion ? t.replaceFont : t.uploadFont}
          {t.fontFormats}
          <input
            type="file"
            accept=".woff2,font/woff2"
            className="sr-only"
            onChange={upload('font')}
          />
        </label>
        <Field label={t.footer} hint={t.footerHint}>
          {(p) => (
            <Input
              {...p}
              maxLength={200}
              value={form.footer}
              onChange={(e) => setForm({ ...form, footer: e.target.value })}
            />
          )}
        </Field>
        <Button type="submit" loading={act.save.isPending} disabled={!colorsOk} className="w-fit">
          {t.saveBrand}
        </Button>
      </form>

      <div
        aria-label={t.preview}
        className="rounded-[16px] bg-surface-0 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
        style={{
          ...brandStyle(preview, assets.fontReady),
          fontFamily:
            form.font === 'custom' && !assets.fontReady
              ? FONT_STACKS.inter
              : FONT_STACKS[form.font],
        }}
      >
        <div className="flex items-center gap-2 text-[13px] text-text-3">
          {assets.logo ? (
            <img src={assets.logo} alt="" className="h-7 w-auto" />
          ) : (
            <span className="font-semibold text-text-1">{form.companyName.trim() || name}</span>
          )}
        </div>
        <h2 className="mt-5 text-[24px] font-semibold tracking-tight">{t.sampleTitle}</h2>
        <p className="mt-1 text-[14px] text-text-2">{t.sampleMeta}</p>
        <div className="mt-4 flex items-start gap-3 rounded-[14px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)]">
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[14px] font-semibold text-accent-text">
            1
          </span>
          <div>
            <p className="font-medium">{t.sampleStep}</p>
            <a
              className="text-[13.5px] text-accent-text underline"
              href="#preview"
              onClick={(e) => e.preventDefault()}
            >
              {t.sampleUrl}
            </a>
          </div>
        </div>
        <button
          type="button"
          className="mt-4 h-10 rounded-full bg-accent px-5 text-[14px] font-semibold text-on-accent"
        >
          {t.button}
        </button>
        <p className="mt-6 text-[12.5px] text-text-3">{form.footer.trim() || t.madeWith}</p>
        {colorsOk && (
          <CoverPreview
            company={form.companyName.trim() || name}
            logo={assets.logo}
            accent={form.accent}
            cover={form.coverColor}
            highlight={form.highlightColor}
          />
        )}
      </div>
    </section>
  );
}

/** Cor opcional: vazia = automática (calculada a partir da cor principal). */
function OptionalColor({
  label,
  hint,
  auto,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  auto: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const t = brandText(useLocale());
  return (
    <Field label={label} hint={value === null ? hint + t.nowAuto : hint}>
      {(p) => (
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="color"
            aria-label={t.pickNamed(label)}
            className="size-11 cursor-pointer rounded-[10px] border-0 bg-transparent"
            value={value && isHexColor(value) ? value : auto}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
          />
          <Input
            {...p}
            className="w-32 font-mono"
            value={value ?? ''}
            placeholder={auto.toUpperCase()}
            maxLength={7}
            onChange={(e) => onChange(e.target.value ? e.target.value : null)}
          />
          {value !== null && (
            <Button size="sm" variant="ghost" type="button" onClick={() => onChange(null)}>
              {t.automatic}
            </Button>
          )}
        </div>
      )}
    </Field>
  );
}

/** Capa das exportações (PowerPoint, HTML e PDF), em ponto pequeno. */
function CoverPreview({
  company,
  logo,
  accent,
  cover,
  highlight,
}: {
  company: string;
  logo: string | null;
  accent: string;
  cover: string | null;
  highlight: string | null;
}) {
  const p = editorialPalette(accent, cover, highlight);
  const t = brandText(useLocale());
  return (
    <figure className="mt-6">
      <figcaption className="mb-2 text-[12.5px] text-text-3">{t.coverPreview}</figcaption>
      <div
        className="relative aspect-video overflow-hidden rounded-[8px] font-normal"
        style={{ background: '#' + p.dark, color: '#' + p.onDark }}
        data-cover
      >
        <span className="absolute left-[5%] top-[6%] text-[8px] uppercase tracking-[.14em]">
          {company} · {t.guide}
        </span>
        <div className="absolute bottom-[26%] left-[5%] w-[64%]">
          <p className="text-[20px] leading-tight">{t.sampleTitle}</p>
          <i className="mt-2 block h-[3px] w-8" style={{ background: '#' + p.highlight }} />
          <p className="mt-1.5 text-[11px]" style={{ color: '#' + p.highlight }}>
            11/10/2026
          </p>
        </div>
        <div
          className="absolute inset-y-0 right-0 flex w-[22.5%] flex-col justify-end p-[3%] pb-[18%]"
          style={{ background: '#' + p.column, color: '#' + p.onColumn }}
        >
          {logo && <img src={logo} alt="" className="absolute left-[12%] top-[6%] h-5 w-auto" />}
          <span className="text-[26px] leading-none">03</span>
          <span className="mt-1 text-[7px] uppercase tracking-[.14em]">{t.steps}</span>
        </div>
      </div>
    </figure>
  );
}
